import { ChevronDown, Typography } from '@sonnen/web-ui';
import clsx from 'clsx';
import { useState } from 'react';
import { CurrentUserSite } from '../../../graphql/generated';
import { siteToOptionType } from '../../../utils/utils';
import styles from './SiteSelector.module.css';

export type SiteSelectorProps = {
  sites: CurrentUserSite[];
  selectedSite: CurrentUserSite;
  onSiteChange: (site: CurrentUserSite) => void;
  batteryLabel: string;
  contractLabel: string;
};

export const SiteSelector = ({
  sites,
  selectedSite,
  onSiteChange,
  batteryLabel,
  contractLabel,
}: SiteSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSiteSelect = (site: CurrentUserSite) => {
    if (site.id !== selectedSite?.id) {
      onSiteChange(site);
      setIsOpen(false);
    }
  };

  const selectedOption = siteToOptionType(selectedSite, batteryLabel, contractLabel);

  return (
    <div
      className={styles.siteSelector}
      // React 19: Ref callback with cleanup function
      ref={(element) => {
        if (!element || !isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
          if (!element.contains(event.target as Node)) {
            setIsOpen(false);
          }
        };

        document.addEventListener('mousedown', handleClickOutside);

        // Return cleanup function (React 19 feature)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }}
      data-testid="site-selector"
    >
      <button
        className={clsx(styles.selectorButton, { [styles.open]: isOpen })}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="site-selector-button"
        aria-label={`Selected site: ${selectedOption.label}`}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-haspopup="listbox"
      >
        <span className={styles.siteText}>
          <Typography.Label1>
            {selectedSite.address.streetName} {selectedSite.address.streetNumber}
          </Typography.Label1>
        </span>
        <span className={styles.chevronIcon}>
          <ChevronDown size="sm" />
        </span>
      </button>

      {isOpen && (
        <div
          className={styles.dropdownMenu}
          role="listbox"
          aria-label="Site selection options"
          data-testid="site-dropdown-menu"
        >
          <div className={styles.dropdownMenuContent}>
            {sites.map((site) => {
              const isSelected = site.id === selectedSite?.id;

              return (
                <button
                  key={site.id}
                  className={clsx(styles.menuItem, { [styles.selected]: isSelected })}
                  onClick={() => handleSiteSelect(site)}
                  role="option"
                  aria-selected={isSelected ? 'true' : 'false'}
                  data-testid={`site-option-${site.id}`}
                >
                  <Typography.Label1>
                    {site.address.streetName} {site.address.streetNumber}, {site.address.postalCode}{' '}
                    {site.address.city}
                  </Typography.Label1>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
